/**
 * SIGNALS-PRACTICE-LENS-3W curator practice assignment — React binding.
 *
 * All behaviour lives in the framework-free session (practice-assignment.ts);
 * this hook only creates one session per mounted control, subscribes to it, and
 * binds the refresh boundary to the query client.
 *
 * The session is created once and is touched only from event handlers and its
 * own request callbacks, never during render, so a StrictMode double-render or
 * a parent re-render can neither mint nor rotate an event key. The effect's
 * cleanup detaches it, which forgets all state and ignores late responses; the
 * StrictMode mount/unmount/mount cycle re-attaches the same session.
 *
 * Nothing retries automatically: every retry is an explicit curator action.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { browserDecisionKey } from "@/features/curator-queue/decision-intent";
import { getSignalPracticeAssignments, submitPracticeAssignment } from "./practice-assignment-api";
import {
  createPracticeAssignmentSession,
  type PracticeAssignmentSession,
  type PracticeSessionSnapshot,
} from "./practice-assignment";

export function useCuratorPracticeAssignments(signalId: string): {
  session: PracticeAssignmentSession;
  snapshot: PracticeSessionSnapshot;
} {
  const queryClient = useQueryClient();
  const [session] = useState(() =>
    createPracticeAssignmentSession({
      signalId,
      newKey: browserDecisionKey,
      read: getSignalPracticeAssignments,
      write: submitPracticeAssignment,
      // Invalidate only - never reset or remove - so the feed keeps its loaded
      // pages and cursor and simply refetches them.
      invalidate: (queryKeys) => {
        for (const queryKey of queryKeys) {
          void queryClient.invalidateQueries({ queryKey: [...queryKey] });
        }
      },
    })
  );

  useEffect(() => {
    session.attach();
    return () => session.detach();
  }, [session]);

  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  return { session, snapshot };
}
